import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { api, errMsg, eur, fileUrl, uploadImage, CATEGORIES } from "@/lib/api";
import { Page } from "@/components/Page";

const inp = "h-12 w-full rounded-xl border border-white/10 bg-[#0A0A0B] px-4 text-sm outline-none focus:border-[#D4AF37]/60";
const CONDITIONS = ["Neu mit Etikett", "Neu", "Sehr gut", "Gut", "Zufriedenstellend"];

export default function Sell() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [rate, setRate] = useState(0.1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", brand: "", category: "Oberteil", size: "", condition: "Sehr gut", price: "", image_paths: [], paypal_email: "", wardrobe_item_id: null });

  useEffect(() => {
    api.get("/market/config").then(({ data }) => setRate(data.commission_rate));
    const from = params.get("from");
    if (from) api.get("/wardrobe").then(({ data }) => {
      const w = data.find((x) => x.id === from);
      if (w) setForm((f) => ({ ...f, title: w.name, brand: w.brand, category: w.category, image_paths: w.image_path ? [w.image_path] : [], wardrobe_item_id: w.id }));
    });
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const price = parseFloat(form.price) || 0;
  const commission = Math.round(price * rate * 100) / 100;

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - form.image_paths.length);
    setBusy(true);
    try {
      const paths = [];
      for (const f of files) paths.push(await uploadImage(f));
      setForm((s) => ({ ...s, image_paths: [...s.image_paths, ...paths] }));
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_paths.length) return toast.error("Mindestens ein Foto hinzufügen");
    setBusy(true);
    try {
      const { data } = await api.post("/listings", { ...form, price });
      toast.success("Dein Artikel ist online!");
      navigate(`/marktplatz/${data.id}`);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <Page over="Verkaufen" title="Artikel anbieten" sub="Fotos hochladen, Preis festlegen, fertig. Bezahlt wird sicher über PayPal." testid="sell-page">
      <form onSubmit={submit} className="grid grid-cols-1 gap-8 lg:grid-cols-12" data-testid="sell-form">
        <div className="space-y-5 lg:col-span-7">
          <div className="surface p-6">
            <p className="label-over mb-4">Fotos (max. 6)</p>
            <div className="grid grid-cols-3 gap-3">
              {form.image_paths.map((p) => (
                <div key={p} className="relative aspect-square overflow-hidden rounded-xl border border-white/10">
                  <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => setForm({ ...form, image_paths: form.image_paths.filter((x) => x !== p) })} className="absolute right-2 top-2 rounded-full bg-black/70 p-1"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {form.image_paths.length < 6 && (
                <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 text-xs text-neutral-400 hover:border-[#D4AF37]/50" data-testid="sell-photo-label">
                  <Upload className="h-5 w-5" />{busy ? "Lädt…" : "Hinzufügen"}
                  <input type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFiles} data-testid="sell-photo-input" />
                </label>
              )}
            </div>
          </div>
          <div className="surface space-y-4 p-6">
            <input required value={form.title} onChange={set("title")} placeholder="Titel, z.B. Levi's 501 Vintage" className={inp} data-testid="sell-title-input" />
            <textarea value={form.description} onChange={set("description")} rows={4} placeholder="Beschreibung (Passform, Material, Mängel …)" className={`${inp} h-auto py-3`} data-testid="sell-description-input" />
            <div className="grid grid-cols-2 gap-3">
              <input value={form.brand} onChange={set("brand")} placeholder="Marke" className={inp} data-testid="sell-brand-input" />
              <input value={form.size} onChange={set("size")} placeholder="Größe, z.B. M / 38" className={inp} data-testid="sell-size-input" />
              <select value={form.category} onChange={set("category")} className={inp} data-testid="sell-category-select">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
              <select value={form.condition} onChange={set("condition")} className={inp} data-testid="sell-condition-select">{CONDITIONS.map((c) => <option key={c}>{c}</option>)}</select>
            </div>
          </div>
        </div>
        <aside className="lg:col-span-5">
          <div className="surface sticky top-24 space-y-5 p-6">
            <label className="block"><span className="label-over">Preis (EUR)</span>
              <input required type="number" min="1" step="0.01" value={form.price} onChange={set("price")} placeholder="0,00" className={`${inp} mt-3 text-lg`} data-testid="sell-price-input" />
            </label>
            <label className="block"><span className="label-over">PayPal-E-Mail für deine Auszahlung</span>
              <input required type="email" value={form.paypal_email} onChange={set("paypal_email")} placeholder="du@beispiel.de" className={`${inp} mt-3`} data-testid="sell-paypal-email-input" />
            </label>
            <div className="space-y-2 rounded-xl border border-white/10 bg-[#0A0A0B] p-4 text-sm" data-testid="commission-breakdown">
              <div className="flex justify-between text-neutral-400"><span>Verkaufspreis</span><span>{eur(price)}</span></div>
              <div className="flex justify-between text-neutral-400"><span>vyne-Provision ({Math.round(rate * 100)}%)</span><span data-testid="commission-amount">− {eur(commission)}</span></div>
              <div className="flex justify-between border-t border-white/10 pt-2 font-semibold"><span>Du erhältst</span><span className="text-[#D4AF37]" data-testid="payout-amount">{eur(price - commission)}</span></div>
            </div>
            <button type="submit" disabled={busy} className="btn-cream w-full" data-testid="sell-submit-button">Jetzt veröffentlichen</button>
          </div>
        </aside>
      </form>
    </Page>
  );
}
